import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { MetricStrip } from '../components/MetricStrip'
import { DataFreshnessBadge } from '../components/DataFreshnessBadge'
import { TrendChart } from '../components/TrendChart'
import { summarizeAdvertisingProfit } from '../advertising'
import { useProductData } from '../data'
import { jdProfitAfterAfterSales, summarizeJdAfterSalesLoss } from '../jdAfterSalesProfit'
import { useJdAfterSalesFacts } from '../platform'
import {
  JD_STORE_ID,
  productMasterCostDefaults,
  productMasterRecordMap,
} from '../productMaster'
import { calculateSkuProfit, internalProductRecordsByCode } from '../productProfit'
import {
  statisticsPeriodOptions,
  statisticsWindow,
  type StatisticsPeriod,
  visibleDateRange,
} from '../statistics'
import type { ProductRecord } from '../types'
import { metricsAcrossDates, money, percent } from '../utils'

type ProfitTrendMetric =
  | 'profitWithRebate'
  | 'profitWithRebateMargin'
  | 'afterSalesLoss'
  | 'profitAfterAfterSales'
  | 'advertisingSpend'
  | 'profitAfterAdvertising'
  | 'profitAfterAdvertisingMargin'

type ProfitContributionDimension = 'spu' | 'sku'

interface ProfitRow {
  product: ProductRecord
  gmv: number
  profit: number | null
  margin: number | null
  previousProfit: number | null
  previousActive: boolean
  profitDelta: number | null
  active: boolean
}

interface ProfitContributionRow extends ProfitRow {
  activeSkuCount?: number
  coveredSkuCount?: number
}

interface ProfitRiskRow {
  row: ProfitRow
  risk: '亏损' | '利润下降' | '高销售低利润' | '成本缺失'
}

const PROFIT_TREND_OPTIONS: Record<ProfitTrendMetric, {
  title: string
  unit: string
  valueLabel: string
  formatter: (value: number) => string
}> = {
  profitWithRebate: {
    title: '30天含返点利润趋势',
    unit: '金额（元）',
    valueLabel: '含返点利润',
    formatter: (value) => Math.abs(value) >= 10000 ? `${(value / 10000).toFixed(1)}万` : value.toFixed(0),
  },
  profitWithRebateMargin: {
    title: '30天含返点利润率趋势',
    unit: '利润率（%）',
    valueLabel: '含返点利润率',
    formatter: (value) => percent(value * 100),
  },
  afterSalesLoss: {
    title: '30天售后损失趋势',
    unit: '金额（元）',
    valueLabel: '售后损失',
    formatter: (value) => Math.abs(value) >= 10000 ? `${(value / 10000).toFixed(1)}万` : value.toFixed(0),
  },
  profitAfterAfterSales: {
    title: '30天扣售后利润趋势',
    unit: '金额（元）',
    valueLabel: '扣售后利润',
    formatter: (value) => Math.abs(value) >= 10000 ? `${(value / 10000).toFixed(1)}万` : value.toFixed(0),
  },
  advertisingSpend: {
    title: '30天广告费趋势',
    unit: '金额（元）',
    valueLabel: '广告费',
    formatter: (value) => Math.abs(value) >= 10000 ? `${(value / 10000).toFixed(1)}万` : value.toFixed(0),
  },
  profitAfterAdvertising: {
    title: '30天广告后利润趋势',
    unit: '金额（元）',
    valueLabel: '广告后利润',
    formatter: (value) => Math.abs(value) >= 10000 ? `${(value / 10000).toFixed(1)}万` : value.toFixed(0),
  },
  profitAfterAdvertisingMargin: {
    title: '30天广告后利润率趋势',
    unit: '利润率（%）',
    valueLabel: '广告后利润率',
    formatter: (value) => percent(value * 100),
  },
}

const signedMoney = (value: number) => `${value >= 0 ? '+' : '−'}${money(Math.abs(value))}`

const median = (values: number[]) => {
  if (!values.length) return 0
  const ordered = values.slice().sort((left, right) => left - right)
  const middle = Math.floor(ordered.length / 2)
  return ordered.length % 2
    ? ordered[middle]
    : (ordered[middle - 1] + ordered[middle]) / 2
}

function ProductCell({ row }: { row: ProfitRow }) {
  return (
    <Link className="profit-product-link" to={`/product/sku/${row.product.id}`}>
      {row.product.image
        ? <img src={row.product.image} alt="" loading="lazy" />
        : <span>无图</span>}
      <span>
        <strong>{row.product.name}</strong>
        <small>SKU {row.product.jdSkuId || row.product.id} · 商家SKU {row.product.merchantSku || '—'}</small>
      </span>
    </Link>
  )
}

function ContributionProductCell({
  dimension,
  row,
}: {
  dimension: ProfitContributionDimension
  row: ProfitContributionRow
}) {
  if (dimension === 'sku') return <ProductCell row={row} />
  return (
    <Link className="profit-product-link" to={`/product/spu/${row.product.id}`}>
      {row.product.image
        ? <img src={row.product.image} alt="" loading="lazy" />
        : <span>无图</span>}
      <span>
        <strong title={row.product.nameNote ? row.product.name : undefined}>{row.product.nameNote || row.product.name}</strong>
        <small>
          SPU {row.product.id} · 已覆盖 {row.coveredSkuCount || 0}/{row.activeSkuCount || 0} 个成交SKU
        </small>
      </span>
    </Link>
  )
}

export function ProfitCenterPage() {
  const {
    advertisingCosts,
    data,
    internalProductInfo,
    productMaster,
    segmentIndex,
    selectedDate,
  } = useProductData()
  const afterSalesFacts = useJdAfterSalesFacts()
  const [statisticsPeriod, setStatisticsPeriod] = useState<StatisticsPeriod>('last7')
  const [trendMetric, setTrendMetric] = useState<ProfitTrendMetric>('profitAfterAdvertising')
  const [contributionDimension, setContributionDimension] = useState<ProfitContributionDimension>('spu')
  const productMasterRecords = useMemo(() => productMasterRecordMap(productMaster, JD_STORE_ID), [productMaster])
  const costDefaults = useMemo(() => productMasterCostDefaults(productMaster, JD_STORE_ID), [productMaster])
  const internalRecordsByCode = useMemo(
    () => internalProductRecordsByCode(internalProductInfo?.records || []),
    [internalProductInfo],
  )
  const period = useMemo(
    () => data ? statisticsWindow(statisticsPeriod, selectedDate, data.meta.dates) : null,
    [data, selectedDate, statisticsPeriod],
  )

  const view = useMemo(() => {
    if (!data || !period) return null
    const spus = data.products.filter((product) => product.level === 'spu')
    const skus = data.products.filter((product) => product.level === 'sku')
    const firstDateIndex = period.dates.length ? data.meta.dates.indexOf(period.dates[0]) : -1
    const previousDates = firstDateIndex > 0
      ? data.meta.dates.slice(Math.max(0, firstDateIndex - period.dates.length), firstDateIndex)
      : []
    const previousWindowComplete = previousDates.length > 0
      && previousDates.length === period.dates.length
    const calculateProfit = (product: ProductRecord, dates: string[]) => {
      const result = calculateSkuProfit(
        product,
        dates,
        selectedDate,
        productMasterRecords,
        costDefaults,
        internalRecordsByCode,
        segmentIndex,
      )
      return {
        gmv: result.gmv,
        profit: result.profitWithRebate,
        margin: result.profitWithRebate !== null && result.gmv > 0
          ? result.profitWithRebate / result.gmv
          : null,
        active: result.active,
      }
    }
    const rows: ProfitRow[] = skus.map((product) => {
      const current = calculateProfit(product, period.dates)
      const previous = previousWindowComplete ? calculateProfit(product, previousDates) : null
      const previousProfit = previous
        ? previous.active ? previous.profit : 0
        : null
      return {
        product,
        gmv: current.gmv,
        profit: current.profit,
        margin: current.margin,
        previousProfit,
        previousActive: previous?.active || false,
        profitDelta: current.profit !== null && previousProfit !== null
          ? current.profit - previousProfit
          : null,
        active: current.active,
      }
    })
    const activeRows = rows.filter((row) => row.active)
    const coveredRows = activeRows.filter((row) => row.profit !== null)
    const totalGmv = spus.reduce(
      (sum, product) => sum + metricsAcrossDates(product, period.dates).gmv,
      0,
    )
    const coveredGmv = coveredRows.reduce((sum, row) => sum + row.gmv, 0)
    const profitWithRebate = coveredRows.length
      ? coveredRows.reduce((sum, row) => sum + (row.profit || 0), 0)
      : activeRows.length ? null : 0
    const profitWithRebateMargin = profitWithRebate !== null && coveredGmv > 0
      ? profitWithRebate / coveredGmv
      : null
    const afterSales = summarizeJdAfterSalesLoss(
      afterSalesFacts.data || null,
      period.dates,
      skus,
      productMasterRecords,
      internalRecordsByCode,
      costDefaults,
    )
    const profitAfterAfterSales = jdProfitAfterAfterSales(profitWithRebate, afterSales)
    const advertising = summarizeAdvertisingProfit(
      advertisingCosts,
      period.dates,
      totalGmv,
      profitAfterAfterSales,
    )
    const previousRows = previousWindowComplete
      ? skus.map((product) => calculateProfit(product, previousDates))
      : []
    const previousActiveRows = previousRows.filter((row) => row.active)
    const previousCoveredRows = previousActiveRows.filter((row) => row.profit !== null)
    const previousProfit = !previousWindowComplete
      ? null
      : previousCoveredRows.length
        ? previousCoveredRows.reduce((sum, row) => sum + (row.profit || 0), 0)
        : previousActiveRows.length ? null : 0
    const profitDelta = profitWithRebate !== null && previousProfit !== null
      ? profitWithRebate - previousProfit
      : null
    const lossRows = coveredRows
      .filter((row) => (row.profit || 0) < 0)
      .sort((left, right) => (left.profit || 0) - (right.profit || 0))
    const overallMargin = profitWithRebateMargin || 0
    const medianGmv = median(coveredRows.map((row) => row.gmv))
    const highSalesLowMarginRows = coveredRows
      .filter((row) => row.gmv >= medianGmv && (row.margin || 0) >= 0 && (row.margin || 0) < overallMargin)
      .sort((left, right) => right.gmv - left.gmv)
    const declineRows = coveredRows
      .filter((row) => (row.profitDelta || 0) < 0)
      .sort((left, right) => (left.profitDelta || 0) - (right.profitDelta || 0))
    const missingCostRows = activeRows
      .filter((row) => row.profit === null)
      .sort((left, right) => right.gmv - left.gmv)
    const risks: ProfitRiskRow[] = []
    const riskIds = new Set<string>()
    const addRisk = (candidates: ProfitRow[], risk: ProfitRiskRow['risk']) => {
      for (const row of candidates) {
        if (risks.length >= 10 || riskIds.has(row.product.id)) continue
        riskIds.add(row.product.id)
        risks.push({ row, risk })
      }
    }
    addRisk(lossRows, '亏损')
    addRisk(declineRows, '利润下降')
    addRisk(highSalesLowMarginRows, '高销售低利润')
    addRisk(missingCostRows, '成本缺失')
    const topProfitRows = coveredRows
      .slice()
      .sort((left, right) => (right.profit || 0) - (left.profit || 0))
      .slice(0, 10)
    const rowsBySpu = new Map<string, ProfitRow[]>()
    for (const row of rows) {
      if (!row.product.spuId) continue
      const group = rowsBySpu.get(row.product.spuId) || []
      group.push(row)
      rowsBySpu.set(row.product.spuId, group)
    }
    const topProfitSpuRows = spus
      .map((product): ProfitContributionRow | null => {
        const allChildRows = rowsBySpu.get(product.id) || []
        const childRows = allChildRows.filter((row) => row.active)
        const coveredChildRows = childRows.filter((row) => row.profit !== null)
        if (!coveredChildRows.length) return null
        const profit = coveredChildRows.reduce((sum, row) => sum + (row.profit || 0), 0)
        const coveredChildGmv = coveredChildRows.reduce((sum, row) => sum + row.gmv, 0)
        const previousActiveChildRows = allChildRows.filter((row) => row.previousActive)
        const previousCoveredRows = allChildRows.filter((row) => row.previousActive && row.previousProfit !== null)
        const previousProfit = !previousWindowComplete
          ? null
          : previousCoveredRows.length
            ? previousCoveredRows.reduce((sum, row) => sum + (row.previousProfit || 0), 0)
            : previousActiveChildRows.length ? null : 0
        return {
          product,
          gmv: childRows.reduce((sum, row) => sum + row.gmv, 0),
          profit,
          margin: coveredChildGmv > 0 ? profit / coveredChildGmv : null,
          previousProfit,
          previousActive: allChildRows.some((row) => row.previousActive),
          profitDelta: previousProfit === null ? null : profit - previousProfit,
          active: true,
          activeSkuCount: childRows.length,
          coveredSkuCount: coveredChildRows.length,
        }
      })
      .filter((row): row is ProfitContributionRow => row !== null)
      .sort((left, right) => (right.profit || 0) - (left.profit || 0))
      .slice(0, 10)
    const trendDates = statisticsWindow('last30', selectedDate, data.meta.dates).dates
    const trend = trendDates.map((date) => {
      const dailyRows = skus.map((product) => calculateProfit(product, [date]))
      const activeDailyRows = dailyRows.filter((row) => row.active)
      const coveredDailyRows = activeDailyRows.filter((row) => row.profit !== null)
      const dailyGmv = spus.reduce((sum, product) => sum + metricsAcrossDates(product, [date]).gmv, 0)
      const dailyCoveredGmv = coveredDailyRows.reduce((sum, row) => sum + row.gmv, 0)
      const dailyProfit = coveredDailyRows.length
        ? coveredDailyRows.reduce((sum, row) => sum + (row.profit || 0), 0)
        : activeDailyRows.length ? null : 0
      const dailyAfterSales = summarizeJdAfterSalesLoss(
        afterSalesFacts.data || null,
        [date],
        skus,
        productMasterRecords,
        internalRecordsByCode,
        costDefaults,
      )
      const dailyProfitAfterAfterSales = jdProfitAfterAfterSales(dailyProfit, dailyAfterSales)
      const dailyAdvertising = summarizeAdvertisingProfit(
        advertisingCosts,
        [date],
        dailyGmv,
        dailyProfitAfterAfterSales,
      )
      return {
        date,
        profitWithRebate: dailyProfit,
        profitWithRebateMargin: dailyProfit !== null && dailyCoveredGmv > 0
          ? dailyProfit / dailyCoveredGmv
          : null,
        afterSalesLoss: dailyAfterSales.complete ? dailyAfterSales.totalLoss : null,
        profitAfterAfterSales: dailyProfitAfterAfterSales,
        advertisingSpend: dailyAdvertising.complete ? dailyAdvertising.total : null,
        profitAfterAdvertising: dailyAdvertising.profitAfterAdvertising,
        profitAfterAdvertisingMargin: dailyAdvertising.profitAfterAdvertisingMargin,
      }
    })
    return {
      totalGmv,
      activeSkuCount: activeRows.length,
      coveredSkuCount: coveredRows.length,
      coveredGmv,
      profitWithRebate,
      profitWithRebateMargin,
      profitDelta,
      afterSales,
      profitAfterAfterSales,
      advertising,
      lossCount: lossRows.length,
      lossAmount: lossRows.reduce((sum, row) => sum + (row.profit || 0), 0),
      highSalesLowMarginCount: highSalesLowMarginRows.length,
      missingCostCount: missingCostRows.length,
      topProfitRows,
      topProfitSpuRows,
      risks,
      previousDates,
      trend,
    }
  }, [
    advertisingCosts,
    afterSalesFacts.data,
    costDefaults,
    data,
    internalRecordsByCode,
    period,
    productMasterRecords,
    segmentIndex,
    selectedDate,
  ])

  if (!data || !period || !view) return null
  const periodRange = visibleDateRange(period.dates)
  const trendOption = PROFIT_TREND_OPTIONS[trendMetric]
  const trendData = view.trend
    .filter((point) => typeof point[trendMetric] === 'number')
    .map((point) => ({ date: point.date, value: point[trendMetric] as number }))
  const coverageRate = view.activeSkuCount ? view.coveredSkuCount / view.activeSkuCount : 1
  const profitContext = view.profitDelta === null
    ? '前期或当前成本覆盖不足'
    : `较前期 ${signedMoney(view.profitDelta)}`
  const contributionRows: ProfitContributionRow[] = contributionDimension === 'spu'
    ? view.topProfitSpuRows
    : view.topProfitRows

  return (
    <div className="profit-center-page page-stack">
      <header className="page-title-row profit-center-header">
        <div>
          <h1>利润中心</h1>
          <p>{period.label} · {periodRange} · 商品含返点利润与店铺级广告后结果</p>
        </div>
        <div className="page-title-actions">
          <DataFreshnessBadge date={data.meta.maxDate} label="利润数据截至" />
          <div className="metric-switch" role="group" aria-label="利润统计时间范围">
            {statisticsPeriodOptions.map((item) => (
              <button
                type="button"
                key={item.key}
                className={item.key === statisticsPeriod ? 'is-active' : ''}
                aria-pressed={item.key === statisticsPeriod}
                onClick={() => setStatisticsPeriod(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {!period.complete && (
        <p className="period-coverage-warning">
          请求范围为 {period.startDate} 至 {period.endDate}，当前只覆盖 {periodRange}；以下按现有数据计算。
        </p>
      )}

      <section className="profit-center-section profit-result-section">
        <div className="section-heading-row">
          <div><h2>利润结果</h2><p>成本缺失SKU不按零成本计算，广告缺失日期不补零</p></div>
          <Link className="text-link" to="/advertising">查看广告费用证据</Link>
        </div>
        <MetricStrip items={[
          { label: '成交金额', value: money(view.totalGmv), context: periodRange },
          {
            label: '含返点利润',
            value: view.profitWithRebate === null ? '—' : money(view.profitWithRebate),
            context: profitContext,
          },
          {
            label: '含返点利润率',
            value: view.profitWithRebateMargin === null ? '—' : percent(view.profitWithRebateMargin * 100),
            context: `覆盖成交 ${money(view.coveredGmv)}`,
          },
          {
            label: '售后损失',
            value: view.afterSales.complete ? money(view.afterSales.totalLoss) : '—',
            context: view.afterSales.complete
              ? `二退 ${view.afterSales.secondReturnOrderCount}单/${view.afterSales.secondReturnItemCount}件 · 货损覆盖 ${view.afterSales.coveredDamageItems}/${view.afterSales.secondReturnItemCount}件`
              : `售后数据覆盖 ${view.afterSales.coveredDays}/${view.afterSales.requestedDays} 天`,
          },
          {
            label: '扣售后利润',
            value: view.profitAfterAfterSales === null ? '—' : money(view.profitAfterAfterSales),
            context: view.afterSales.complete
              ? `往返运费 ${money(view.afterSales.freightLoss)} + 纸箱 ${money(view.afterSales.packagingLoss)} + 货损 ${money(view.afterSales.damageLoss)}`
              : '售后数据未完整覆盖窗口',
          },
          {
            label: '广告费',
            value: view.advertising.complete ? money(view.advertising.total) : '—',
            context: `覆盖 ${view.advertising.coveredDays}/${view.advertising.requestedDays} 天`,
          },
          {
            label: '广告后利润',
            value: view.advertising.profitAfterAdvertising === null
              ? '—'
              : money(view.advertising.profitAfterAdvertising),
            context: '扣售后利润−广告费，店铺级',
          },
          {
            label: '广告后利润率',
            value: view.advertising.profitAfterAdvertisingMargin === null
              ? '—'
              : percent(view.advertising.profitAfterAdvertisingMargin * 100),
            context: '广告后利润 ÷ 成交金额',
          },
        ]} />
      </section>

      <section className="profit-center-section">
        <div className="section-heading-row">
          <div><h2>利润桥</h2><p>从商品经营利润到店铺广告后结果</p></div>
        </div>
        <div className="profit-bridge">
          <article>
            <span>商品含返点利润</span>
            <strong>{view.profitWithRebate === null ? '—' : money(view.profitWithRebate)}</strong>
            <small>已覆盖 {view.coveredSkuCount}/{view.activeSkuCount} 个成交SKU</small>
          </article>
          <b aria-hidden="true">−</b>
          <article>
            <span>售后损失</span>
            <strong>{view.afterSales.complete ? money(view.afterSales.totalLoss) : '—'}</strong>
            <small>二退往返运费＋纸箱报废＋产品5%货损，一退不计损失</small>
          </article>
          <b aria-hidden="true">−</b>
          <article>
            <span>店铺广告费</span>
            <strong>{view.advertising.complete ? money(view.advertising.total) : '—'}</strong>
            <small>常规京准通＋品牌合约＋京东联盟</small>
          </article>
          <b aria-hidden="true">＝</b>
          <article className={view.advertising.profitAfterAdvertising !== null && view.advertising.profitAfterAdvertising < 0 ? 'is-negative' : 'is-result'}>
            <span>广告后利润</span>
            <strong>{view.advertising.profitAfterAdvertising === null ? '—' : money(view.advertising.profitAfterAdvertising)}</strong>
            <small>经营估算，不表述为财务净利润</small>
          </article>
        </div>
      </section>

      <div className="profit-center-main-grid">
        <section className="profit-center-section">
          <div className="section-heading-row">
            <div><h2>{trendOption.title}</h2><p>{trendOption.unit}</p></div>
            <div className="metric-switch profit-trend-switch" role="group" aria-label="利润趋势指标">
              {Object.entries(PROFIT_TREND_OPTIONS).map(([key, option]) => (
                <button
                  type="button"
                  key={key}
                  className={key === trendMetric ? 'is-active' : ''}
                  aria-pressed={key === trendMetric}
                  onClick={() => setTrendMetric(key as ProfitTrendMetric)}
                >
                  {option.valueLabel}
                </button>
              ))}
            </div>
          </div>
          <TrendChart
            data={trendData}
            valueFormatter={trendOption.formatter}
            valueLabel={trendOption.valueLabel}
            ariaLabel={trendOption.title}
          />
        </section>
        <aside className="profit-center-section profit-health-panel">
          <div className="section-heading-row"><h2>利润健康</h2></div>
          <dl>
            <div>
              <dt>成本覆盖率</dt>
              <dd>{percent(coverageRate * 100)}</dd>
              <small>{view.missingCostCount}个成交SKU成本缺失</small>
            </div>
            <div>
              <dt>亏损SKU</dt>
              <dd className={view.lossCount ? 'is-negative' : ''}>{view.lossCount}个</dd>
              <small>合计 {signedMoney(view.lossAmount)}</small>
            </div>
            <div>
              <dt>高销售低利润</dt>
              <dd>{view.highSalesLowMarginCount}个</dd>
              <small>成交高于中位数且利润率低于整体</small>
            </div>
          </dl>
          <Link className="profit-health-link" to="/product-master">补齐或复核商品成本</Link>
        </aside>
      </div>

      <div className="profit-tables-grid">
        <section className="profit-center-section">
          <div className="section-heading-row">
            <div>
              <h2>利润贡献榜</h2>
              <p>
                {contributionDimension === 'spu'
                  ? '按SPU汇总旗下官方SKU的含返点利润'
                  : '按SKU含返点利润排序'}
              </p>
            </div>
            <div className="metric-switch" role="group" aria-label="利润贡献榜统计维度">
              <button
                type="button"
                className={contributionDimension === 'spu' ? 'is-active' : ''}
                aria-pressed={contributionDimension === 'spu'}
                onClick={() => setContributionDimension('spu')}
              >
                按SPU
              </button>
              <button
                type="button"
                className={contributionDimension === 'sku' ? 'is-active' : ''}
                aria-pressed={contributionDimension === 'sku'}
                onClick={() => setContributionDimension('sku')}
              >
                按SKU
              </button>
            </div>
          </div>
          <div className="table-responsive">
            <table className="profit-center-table">
              <thead><tr><th>商品</th><th className="numeric">成交金额</th><th className="numeric">含返点利润</th><th className="numeric">利润率</th><th className="numeric">较前期</th></tr></thead>
              <tbody>
                {contributionRows.map((row) => (
                  <tr key={row.product.id}>
                    <td><ContributionProductCell dimension={contributionDimension} row={row} /></td>
                    <td className="numeric">{money(row.gmv)}</td>
                    <td className={`numeric ${(row.profit || 0) >= 0 ? 'is-positive' : 'is-negative'}`}>{money(row.profit || 0)}</td>
                    <td className="numeric">{row.margin === null ? '—' : percent(row.margin * 100)}</td>
                    <td className={`numeric ${(row.profitDelta || 0) >= 0 ? 'is-positive' : 'is-negative'}`}>
                      {row.profitDelta === null ? '—' : signedMoney(row.profitDelta)}
                    </td>
                  </tr>
                ))}
                {!contributionRows.length && (
                  <tr>
                    <td colSpan={5} className="empty-cell">
                      当前范围没有可计算的{contributionDimension === 'spu' ? 'SPU' : 'SKU'}利润贡献。
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="profit-center-section">
          <div className="section-heading-row">
            <div><h2>利润风险榜</h2><p>亏损优先，其次利润下降、高销售低利润和成本缺失</p></div>
          </div>
          <div className="table-responsive">
            <table className="profit-center-table">
              <thead><tr><th>商品</th><th>风险</th><th className="numeric">成交金额</th><th className="numeric">含返点利润</th><th className="numeric">较前期</th></tr></thead>
              <tbody>
                {view.risks.map(({ row, risk }) => (
                  <tr key={row.product.id}>
                    <td><ProductCell row={row} /></td>
                    <td><span className={`profit-risk-label risk-${risk}`}>{risk}</span></td>
                    <td className="numeric">{money(row.gmv)}</td>
                    <td className={`numeric ${(row.profit || 0) < 0 ? 'is-negative' : ''}`}>
                      {row.profit === null ? '—' : money(row.profit)}
                    </td>
                    <td className={`numeric ${(row.profitDelta || 0) >= 0 ? 'is-positive' : 'is-negative'}`}>
                      {row.profitDelta === null ? '—' : signedMoney(row.profitDelta)}
                    </td>
                  </tr>
                ))}
                {!view.risks.length && <tr><td colSpan={5} className="empty-cell">当前范围没有识别到利润风险。</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <footer className="methodology-note">
        含返点利润使用当前最新产品成本，并计入包装、快递、赠品、运费险、平台扣点、税点和返点；成本缺失SKU不按0成本计算。扣售后利润＝含返点利润−售后损失（D-211）：售后损失只对退款成功且申请时已发货的二退计算＝往返运费（发出与退回两笔，京东快递运费×2）＋纸箱报废（包装成本）＋退回产品按成本5%货损；一退（未发货退款）不产生损失；退款金额不直接扣利润。售后按服务单申请日归日，与商智成交的下单日不同轴，为参考值；缺成本或未拆分的二退件数不按0成本伪造货损；售后数据未完整覆盖窗口时显示缺失不补零。广告后利润＝扣售后利润−广告费，只在三类广告费用日期完整时计算，广告费保持店铺级，不伪分摊到SKU。
      </footer>
    </div>
  )
}
